import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./baseQueryWithReauth";

export interface UserResponse {
  uid: string;
  username: string;
  email: string;
  name: string;
  surname: string;
  role: string;
  image: {
    url: string;
  };
  createdAt: string;
  bio: string;
  tradingStyle: string;
  tradingMentors: string;
  followingCount: number;
  followersCount: number;
  postsCount: number;
  likesPerPost: number;
  rankPercentile: number;
  balance: number;
  isSubscribed: boolean;
  socialMedia: { url: string; title?: string }[];
  likesCount: number;
  zapCount: number;
  isPremium: boolean;
  totalEarned?: number;
  // TODO(WM-1368): assumed field — the user's rank among all earners (1 = top earner).
  earnerRank?: number;
  topEarnerRank?: number;
}
export interface FollowResponse {
  createdAt: string;
  userId: number;
  instrumentId: number;
  message: string;
}
export interface SearchResult<T> {
  type: string;
  score: number;
  data: T;
}

export interface UniversalSearchResponse<T> {
  results: SearchResult<T>[];
  total: number;
  query: string;
  type: string;
}

export interface SearchResult<T> {
  type: string;
  score: number;
  data: T;
}

export interface UserListItem {
  uid: string;
  username: string;
  image: {
    url: string;
    mimeType?: string;
  };
  bio: string;
}
export interface UserSearchItem {
  uid: string;
  username: string;
  image: string;
  bio: string;
}
export interface PaginatedUsersResponse {
  data: UserListItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
  };
}

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["User", "UserSidebar"],
  endpoints: (build) => ({
    getUserSideBar: build.query({
      query: () => "/users/sidebar",
      providesTags: [{ type: "UserSidebar", id: "SELF" }],
    }),

    getUserByUid: build.query<UserResponse, string>({
      query: (uid) => `/users/profile/${uid}`,
      providesTags: (_res, _err, uid) => [{ type: "User", id: uid }],
    }),
    followUser: build.mutation<FollowResponse, string>({
      query: (uid) => ({
        url: `/users/${uid}/follow`,
        method: "POST",
      }),
    }),
    unfollowUser: build.mutation<FollowResponse, string>({
      query: (uid) => ({
        url: `/users/${uid}/unfollow`,
        method: "POST",
      }),
    }),
    // todo fix type
    getUniversalSearch: build.query<
      UniversalSearchResponse<any>,
      { query: string; type: string; limit?: number }
    >({
      query: ({ query, type, limit = 10 }) => ({
        url: "/search",
        method: "GET",
        params: { query, type, limit },
      }),
      keepUnusedDataFor: 0,
    }),

    getAutocomplete: build.query({
      query: ({ query, type }) => ({
        url: "/search/autocomplete",
        method: "GET",
        params: { query, type },
      }),
    }),

    getUserFollowers: build.query<
      PaginatedUsersResponse,
      { uid: string; page?: number; limit?: number }
    >({
      query: ({ uid, page = 1, limit = 20 }) => ({
        url: `/users/${uid}/followers`,
        method: "GET",
        params: { page, limit },
      }),
      providesTags: (_res, _err, { uid }) => [{ type: "User", id: `${uid}-followers` }],
    }),

    getUserFollowing: build.query<
      PaginatedUsersResponse,
      { uid: string; page?: number; limit?: number }
    >({
      query: ({ uid, page = 1, limit = 20 }) => ({
        url: `/users/${uid}/following`,
        method: "GET",
        params: { page, limit },
      }),
      providesTags: (_res, _err, { uid }) => [{ type: "User", id: `${uid}-following` }],
    }),
  }),
});

export const {
  useGetUserSideBarQuery,
  useGetUserByUidQuery,
  useFollowUserMutation,
  useUnfollowUserMutation,
  useLazyGetUniversalSearchQuery,
  useGetAutocompleteQuery,
  useGetUniversalSearchQuery,
  useGetUserFollowersQuery,
  useGetUserFollowingQuery,
} = userApi;
